import { ElementRef, inject, Injectable } from '@angular/core';
import { getConfiguration } from '@lx/shared/tokens';
import * as ElfsightEmbedSDK from '@elfsight/embed-sdk';
import { LoggerService } from '@lx/shared/logger';
import { BreakpointObserverService } from '@lx/shared/utils-dom';
import { take } from 'rxjs/operators';

/**
 * Service to handle the Elfsight acquisition disclaimer widget display
 * Centralizes the logic for loading and displaying the widget across components
 * Widget is only displayed on desktop devices
 */
@Injectable({
  providedIn: 'root',
})
export class AcquisitionDisclaimerWidgetService {
  private readonly configuration = getConfiguration();
  private readonly logger = inject(LoggerService);
  private readonly breakpointObserver = inject(BreakpointObserverService);

  /**
   * Loads and displays the Elfsight acquisition disclaimer widget in the provided container
   * Only displays the widget on desktop devices when a valid widget ID is configured
   * @param container - The ElementRef containing the native element where the widget should be displayed
   */
  loadWidget(container: ElementRef | undefined): void {
    const widgetID = this.configuration.ELFSIGHT_ACQUISITION_DISCLAIMER_WIDGET as string;

    if (!container || !widgetID) {
      return;
    }

    // Only load widget on desktop devices
    this.breakpointObserver.desktop$.pipe(take(1)).subscribe((desktop) => {
      if (!desktop) {
        return;
      }

      if (!ElfsightEmbedSDK.displayWidget || typeof ElfsightEmbedSDK.displayWidget !== 'function') {
        this.logger.error('Elfsight SDK displayWidget method is not available');
        return;
      }

      try {
        ElfsightEmbedSDK.displayWidget(container.nativeElement, widgetID);
      } catch (error) {
        this.logger.error('Failed to display Elfsight widget:', error);
      }
    });
  }
}
